import { FC, useEffect, useState } from 'react';
import { AssistantStreamStatus } from '@/types/assistant';

interface Props {
  /** Nome da tool em execução (vem do evento `tool` do SSE). Sobrescreve a rotação. */
  activeTool?: string | null;
  /** Único status legal (compat — testes). Em produção use `legalSteps`. */
  legalStatus?: AssistantStreamStatus | null;
  /**
   * J07/fix: histórico ACUMULADO dos status de domínio (category=legal ou sharepoint).
   * O parent acumula via updater funcional (à prova de batching do React) pra
   * mostrar o processo real do bot — não uma frase genérica rotativa.
   */
  legalSteps?: AssistantStreamStatus[];
  /** Assistente confidencial (ex.: H.Lex): antes do 1º passo chegar, mostra um label do
   * domínio ("Lendo o documento…") em vez da rotação genérica. */
  legal?: boolean;
  /** Assistente SharePoint (H.Find): walkthrough das fases de indexação/casamento (S06). */
  sharepoint?: boolean;
  /** Assistente de apresentações (H.Slide): walkthrough da geração do deck (D04). */
  deck?: boolean;
  /** Assistente de revisão de documentos (H.Rev): walkthrough da revisão (Q06). */
  quality?: boolean;
}

const TOOL_LABELS: Record<string, string> = {
  web_search: 'Buscando na web…',
  create_text_document: 'Gerando documento…',
  create_spreadsheet: 'Gerando planilha…',
  generate_chart: 'Desenhando o gráfico…',
  analyze_spreadsheet: 'Lendo sua planilha…',
  remember_fact: 'Anotando o que importa…',
  analyze_contract: 'Analisando o contrato…',
  export_contract_analysis: 'Montando a planilha da análise…',
  // S06: tools do H.Find.
  match_drl: 'Conferindo o DRL na pasta…',
  export_drl_result: 'Montando a planilha do DRL…',
  reindex_sharepoint_folder: 'Re-indexando a pasta…',
};

const IDLE_STATES = ['Pensando…', 'Organizando as ideias…', 'Quase lá…'];

interface TraceStep {
  key: string;
  label: string;
  found?: boolean | null; // só pra cláusulas: true=encontrada, false=não
}

/**
 * Converte os status de domínio acumulados (legal OU sharepoint) numa trilha ordenada de
 * passos REAIS do bot. No jurídico (J07): funde detecção de tipo e cada cláusula vira um
 * passo. No SharePoint (S06): fases da indexação + "casando item X/Y". Fases desconhecidas
 * são ignoradas.
 */
function buildDomainTrace(steps: AssistantStreamStatus[]): TraceStep[] {
  const order: string[] = [];
  const map = new Map<string, TraceStep>();
  const put = (key: string, label: string, found?: boolean | null) => {
    if (!map.has(key)) order.push(key);
    map.set(key, { key, label, found });
  };
  for (const s of steps) {
    if (!s.phase) continue;
    if (s.category === 'legal') {
      switch (s.phase) {
        case 'lendo':
          put('lendo', 'Lendo o contrato');
          break;
        case 'detectando_tipo':
          put('tipo', 'Identificando o tipo de contrato');
          break;
        case 'tipo_detectado':
          put('tipo', s.contract_type ? `Tipo: ${s.contract_type}` : 'Tipo identificado');
          break;
        case 'recuperando':
          put('recuperando', 'Localizando as cláusulas relevantes');
          break;
        case 'analisando':
          put('analisando', 'Analisando as cláusulas');
          break;
        case 'clausula':
          put(`cl:${s.clause ?? ''}`, s.clause || 'Cláusula', s.found ?? null);
          break;
        case 'cruzando':
          put('cruzando', 'Cruzando evento → pagamento');
          break;
        case 'montando_tabela':
          put('montando', 'Montando a tabela de cláusulas');
          break;
        default:
          break;
      }
    } else if (s.category === 'sharepoint') {
      switch (s.phase) {
        case 'lendo_drl':
          put('lendo_drl', 'Lendo o DRL');
          break;
        case 'drl_lido':
          put('lendo_drl', s.total ? `DRL lido — ${s.total} itens` : 'DRL lido');
          break;
        case 'indexando':
          put('indexando', 'Indexando a pasta do SharePoint');
          break;
        case 'indice_pronto':
          put('indexando', s.arquivos ? `Índice pronto — ${s.arquivos} arquivos` : 'Índice pronto');
          break;
        case 'casando':
          // 1 passo só, atualizado a cada linha (X/Y), pra não explodir a trilha.
          put(
            'casando',
            s.index && s.total ? `Casando item ${s.index}/${s.total}` : 'Casando os documentos',
          );
          break;
        case 'avaliando_ia':
          put('avaliando_ia', 'Avaliando os candidatos com IA');
          break;
        case 'avaliado_ia':
          put('avaliando_ia', s.done && s.total ? `Avaliando com IA — ${s.done}/${s.total}` : 'Avaliando com IA');
          break;
        case 'montando':
          put('montando', 'Montando o resultado');
          break;
        default:
          break;
      }
    } else if (s.category === 'deck') {
      switch (s.phase) {
        case 'montando':
          put('montando', s.total ? `Montando o roteiro — ${s.total} slides` : 'Montando o roteiro');
          break;
        case 'slide':
          // 1 passo só, atualizado por slide (X/Y), pra não explodir a trilha.
          put(
            'slide',
            s.index && s.total
              ? `Slide ${s.index}/${s.total}${s.heading ? `: ${s.heading}` : ''}`
              : 'Gerando os slides',
          );
          break;
        default:
          break;
      }
    } else if (s.category === 'quality') {
      switch (s.phase) {
        case 'lendo':
          put('lendo', 'Lendo o documento');
          break;
        case 'formatacao':
          put(
            'formatacao',
            typeof s.findings === 'number'
              ? `Conferindo a formatação — ${s.findings} apontamentos`
              : 'Conferindo a formatação',
          );
          break;
        case 'normas':
          put(
            'normas',
            typeof s.findings === 'number'
              ? `Conferindo as legislações — ${s.findings} apontamentos`
              : 'Conferindo as legislações',
          );
          break;
        case 'julgamento':
          put('julgamento', 'Analisando título, siglas e referências');
          break;
        case 'montando':
          put('montando', 'Montando o resultado');
          break;
        default:
          break;
      }
    }
  }
  return order.map((k) => map.get(k) as TraceStep);
}

// Quantos passos concluídos mostrar acima do passo atual (janela rolante).
const TRAIL = 4;

// Fases REAIS que o analyzer do H.Lex percorre (J03). Em prod o stream SSE bufferiza
// e os status `legal` não chegam em tempo real (chegam num burst no fim) — então, no
// agente jurídico, paceamos esta trilha no cliente pra o usuário VER o processo andar.
// Não é fake: são exatamente as fases que o backend executa. Quando os eventos reais
// chegam (`legalSteps`), eles ASSUMEM e mostram cláusula a cláusula.
const SIM_LEGAL_PHASES = [
  'Lendo o documento',
  'Identificando o tipo de contrato',
  'Localizando as cláusulas relevantes',
  'Analisando as cláusulas do playbook',
  'Cruzando evento → pagamento',
  'Preparando a resposta',
];

// S06: fases REAIS do matcher do H.Find (S04). Mesmo motivo do legal — o SSE pode
// bufferizar; paceamos no cliente enquanto os eventos `sharepoint` reais não chegam.
const SIM_SHAREPOINT_PHASES = [
  'Lendo o DRL',
  'Indexando a pasta do SharePoint',
  'Casando os documentos',
  'Avaliando os candidatos com IA',
  'Montando o resultado',
];

// D04: fases da geração do deck (H.Slide). Mesmo motivo do legal/sharepoint — o SSE pode
// bufferizar; paceamos no cliente enquanto os eventos `deck` reais não chegam.
const SIM_DECK_PHASES = [
  'Montando o roteiro',
  'Gerando os slides',
  'Aplicando a identidade da marca',
  'Finalizando a apresentação',
];

// Q06: fases REAIS da revisão do H.Rev (Q03/Q04: lendo → formatacao → normas → julgamento
// → montando). Mesmo motivo dos demais domínios — paceamos no cliente enquanto os eventos
// `quality` reais não chegam.
const SIM_QUALITY_PHASES = [
  'Lendo o documento',
  'Conferindo a formatação',
  'Conferindo as legislações',
  'Analisando título, siglas e referências',
  'Montando o resultado',
];
const SIM_STEP_MS = 2800;

function Dots() {
  return (
    <span className="inline-flex gap-1">
      <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </span>
  );
}

const AssistantThinking: FC<Props> = ({
  activeTool,
  legalStatus,
  legalSteps,
  legal,
  sharepoint,
  deck,
  quality,
}) => {
  const [idx, setIdx] = useState(0);
  const [simIdx, setSimIdx] = useState(0);

  const raw = legalSteps && legalSteps.length ? legalSteps : legalStatus ? [legalStatus] : [];
  const realTrace = buildDomainTrace(raw);
  const hasReal = realTrace.length > 0;
  // Fases do walkthrough conforme o domínio do assistente (legal > sharepoint > deck > quality).
  const simPhases = legal
    ? SIM_LEGAL_PHASES
    : sharepoint
      ? SIM_SHAREPOINT_PHASES
      : deck
        ? SIM_DECK_PHASES
        : quality
          ? SIM_QUALITY_PHASES
          : null;

  // Rotação genérica (só quando não há tool, nem trilha real, nem domínio especializado).
  useEffect(() => {
    if (activeTool || hasReal || simPhases) return;
    setIdx(0);
    const interval = window.setInterval(() => setIdx((i) => (i + 1) % IDLE_STATES.length), 1500);
    return () => window.clearInterval(interval);
  }, [activeTool, hasReal, simPhases]);

  // Walkthrough das fases reais (legal/sharepoint) enquanto os eventos reais não chegam.
  useEffect(() => {
    if (!simPhases || hasReal) return;
    setSimIdx(0);
    const id = window.setInterval(
      () => setSimIdx((i) => Math.min(i + 1, simPhases.length - 1)),
      SIM_STEP_MS,
    );
    return () => window.clearInterval(id);
  }, [simPhases, hasReal]);

  // Decide a trilha a renderizar: eventos REAIS têm prioridade; senão, o walkthrough do domínio.
  const trace: TraceStep[] = hasReal
    ? realTrace
    : simPhases
      ? simPhases.slice(0, simIdx + 1).map((label, i) => ({ key: `sim${i}`, label }))
      : [];

  if (trace.length > 0) {
    const current = trace[trace.length - 1];
    const done = trace.slice(0, -1).slice(-TRAIL);
    return (
      <div data-testid="assistant-thinking" className="text-content-tertiary text-sm py-2 px-4 space-y-1">
        {done.map((s) => (
          <div key={s.key} data-testid="thinking-step" className="flex items-center gap-2 opacity-70">
            <span
              className={`material-symbols-outlined text-sm leading-none ${
                s.found === false ? 'text-content-tertiary' : 'text-accent'
              }`}
            >
              {s.found === false ? 'remove' : 'check'}
            </span>
            <span>{s.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 text-content-secondary">
          <Dots />
          <span data-testid="thinking-label">
            {current.found === false ? `${current.label} —` : current.found ? `${current.label} ✔` : current.label}
          </span>
        </div>
      </div>
    );
  }

  const label = activeTool ? TOOL_LABELS[activeTool] ?? 'Usando uma ferramenta…' : IDLE_STATES[idx];

  return (
    <div
      data-testid="assistant-thinking"
      className="flex items-center gap-2 text-content-tertiary text-sm py-2 px-4"
    >
      <Dots />
      <span data-testid="thinking-label">{label}</span>
    </div>
  );
};

export default AssistantThinking;
